
import { Vue, Component, Watch } from 'vue-property-decorator';
import MapDisclaimer from '@/components/MapLayers/MapDisclaimer.vue';
import MapHelpPopup from '@/components/MapLayers/MapHelpPopup.vue';
import MapLayers from '@/components/MapLayers/MapLayers.vue';
import {
  hucFields,
  basinFields,
  choroplethColors,
  layers,
  mapLayerIds,
} from '@/assets/data/mapLayers';
import mapboxgl, { AnyLayer, MapboxGeoJSONFeature, FillPaint } from 'mapbox-gl';
import { DataFilterActions } from '@/store/actions/dataFilterActions';
@Component({
  components: {
    MapDisclaimer,
    MapLayers,
    MapHelpPopup,
  },
})
export default class Map extends Vue {
  map!: mapboxgl.Map;
  popup = new mapboxgl.Popup({ closeOnClick: false });

  // id of the clicked 'CHROPLETH_LAYER' item used for setting feature-state
  selectedFeatureStateItem: MapboxGeoJSONFeature | null = null;
  // increment each time the choropleth is re-drawn
  choroplethRender = 0;

  // refererence to the first feature layer. We will use this id to place the choropleth below our features.
  firstFeature = layers[0].layerInfo.source.id;
  // This is used to determine if we are currently displaying nodes on the map
  displayingNodes = false;

  mounted() {
    // On mounted we want to fetch the show nodes state from the store
    this.displayingNodes = this.choroplethLayer.showNodes;

    this.map = new mapboxgl.Map({
      accessToken: process.env.VUE_APP_MAPBOX_ACCESS_TOKEN,
      container: this.$refs.map as HTMLElement,
      interactive: true,
      style: 'mapbox://styles/mapbox/light-v10',
      center: [-105.358887, 39.113014],
      zoom: 6,
      maxZoom: 16,
      minZoom: 3,
      pitch: 0,
      bearing: 0,
    });

    this.map.addControl(
      new mapboxgl.NavigationControl({
        showCompass: false,
        showZoom: true,
      }),
      'bottom-right',
    );

    this.map.on('load', () => {
      this.setupFeatureLayers();
      this.setupChoroplethLayer();
    });
    // This listens for the parent resize-map-view and calls mapbox resize
    this.$parent?.$on('resize-map-view', () => {
      this.map.resize();
    });
  }

  get choroplethLayer() {
    return this.$store.getters.choroplethViz;
  }

  get selectedArea() {
    return this.$store.getters.selectedArea;
  }

  get areas() {
    return this.$store.getters.areas;
  }

  removeLayer(layer: string) {
    if (this.map.getLayer(layer)) {
      this.map.removeLayer(layer);
    }
    if (this.map.getSource(layer)) {
      this.map.removeSource(layer);
    }
  }

  setupChoroplethLayer() {
    const layerId = mapLayerIds.CHOROPLETH_LAYER;
    this.removeLayer(layerId);
    this.popup.remove();
    if (this.choroplethLayer.layer) {
      if (this.choroplethLayer.dataSource == 'huc') {
        // Build a source where it gets the huc node layers
        this.map.addSource(layerId, {
          type: 'vector',
          tiles: [
            `${process.env.VUE_APP_ENDPOINT}/layers/${
              this.choroplethLayer.dataSource
            }/${this.choroplethLayer.layer}/${
              this.choroplethLayer.options.length > 0
                ? encodeURIComponent(this.choroplethLayer.options[0].label)
                : ''
            }/{z}/{x}/{y}?fields=${hucFields}`,
          ],
          promoteId: 'id',
          maxzoom: 18,
        });
      } else {
        // Build a source from the new special basins tile server
        this.map.addSource(layerId, {
          type: 'vector',
          tiles: [
            `${process.env.VUE_APP_ENDPOINT}/basins/layers/{z}/{x}/{y}?fields=${basinFields}`,
          ],
          promoteId: 'gid',
          maxzoom: 18,
        });
      }

      const hucPaint: mapboxgl.FillPaint = {
        'fill-color': [
          'case',
          ['==', ['get', 'risk_value'], 'Low'],
          choroplethColors.low,
          ['==', ['get', 'risk_value'], 'Moderate'],
          choroplethColors.moderate,
          ['==', ['get', 'risk_value'], 'High'],
          choroplethColors.high,
          choroplethColors.extreme,
        ],
        'fill-opacity': 0,
        'fill-outline-color': 'black',
      };

      const basinPaint: mapboxgl.FillPaint = {
        'fill-color': [
          'case',
          ['boolean', ['feature-state', 'click'], false],
          'rgba(1, 1, 1, 0.1)',
          'rgba(0, 0, 0, 0)',
        ],
        'fill-opacity': 1,
        'fill-outline-color': 'black',
      };

      this.map.addLayer(
        {
          id: layerId,
          type: 'fill',
          source: layerId,
          'source-layer': 'tile',
          paint:
            this.choroplethLayer.dataSource === 'huc' ? hucPaint : basinPaint,
        },
        this.firstFeature, // place the choropleth below our first feature layer
      );

      if (this.choroplethRender === 0) {
        // only add the click event for this layer once
        this.setupClickEvent(mapLayerIds.CHOROPLETH_LAYER, [
          { name: 'Basin', field: 'basinround' },
          { name: 'Flow Tool Node Name', field: 'flow_tool_node_name' },
          {
            name: 'Risk Value',
            field: 'risk_metric', //name of value on huc_view
          },
          {
            name: 'Risk Score',
            field: 'risk_value',
          },
          {
            name: 'Risk Value',
            field: 'value', // name of value on basins_view
          },
        ]);
      }
      this.choroplethRender += 1;
    }
  }

  // set highlight of clicked item area on map
  setFeatureState(click: boolean) {
    if (this.selectedFeatureStateItem) {
      this.map.setFeatureState(
        {
          source: this.selectedFeatureStateItem.source
            ? this.selectedFeatureStateItem.source
            : mapLayerIds.CHOROPLETH_LAYER,
          sourceLayer: 'tile',
          id: this.selectedFeatureStateItem.id,
        },
        { click: click },
      );
    }
    if (!click) {
      this.popup.remove();
    }
  }

  setupClickEvent(id: string, info: { name: string; field: string }[]) {
    this.map.on('click', id, e => {
      // get all of the features at the click point
      const clickedFeatures = this.map.queryRenderedFeatures(e.point);
      // Fnd the choropleth feature
      const choroplethFeature = clickedFeatures.find(
        x => x.source === 'CHOROPLETH_LAYER',
      );
      if (e.features && e.features[0].source == clickedFeatures[0].source) {
        if (this.selectedFeatureStateItem !== null) {
          this.setFeatureState(false);
        }

        if (e.features[0].id) {
          if (e.features[0].id === this.selectedFeatureStateItem?.id) {
            this.selectedFeatureStateItem = null;
          } else {
            this.selectedFeatureStateItem = e.features[0];
            this.setFeatureState(true);

            this.setSelectedAreaBasedOnFeatureClick(
              e.features[0].properties?.basinround,
            );

            if (e.features[0].properties) {
              const properties = e.features[0].properties;
              const tooltipInfo = info;
              if (choroplethFeature?.properties) {
                properties['risk_value'] =
                  choroplethFeature.properties.risk_value;
                properties['risk_metric'] =
                  choroplethFeature.properties.risk_metric;
              }
              let html = '';
              tooltipInfo.forEach(item => {
                if (properties[item.field] !== undefined) {
                  let colorDot = '';

                  if (item.field === 'risk_value') {
                    const color = (choroplethFeature?.layer.paint as FillPaint)[
                      'fill-color'
                    ];
                    colorDot = `<span style="display: inline-block; width: 10px; height: 10px; background-color: ${color}; border-radius: 50%; margin-left: 5px;"></span>`;
                  }

                  html += `<b>${item.name}:</b> ${colorDot} <span>${
                    properties[item.field]
                  }</span><br/>`;
                }
              });
              this.popup.setLngLat(e.lngLat).setHTML(html);
              // If we are currently displaying nodes on the map, only show the popup if the clicked feature is not the choropleth layer
              if (this.displayingNodes) {
                if (id !== mapLayerIds.CHOROPLETH_LAYER) {
                  this.popup.addTo(this.map);
                }
              } else {
                this.popup.addTo(this.map);
              }
            }
          }
        }
      }
    });
  }

  setupFeatureLayers() {
    // iterate through each layer and add to map.
    layers.forEach(layer => {
      const source = layer.layerInfo.source;
      this.map.addSource(source.id, {
        type: 'vector',
        tiles: [
          `${process.env.VUE_APP_ENDPOINT}/layers/${source.id}/{z}/{x}/{y}?fields=${source.fields}`,
        ],
        promoteId: 'gid',
        maxzoom: 18,
      });

      // add layer to the amp
      this.map.addLayer(layer.layerInfo.layer as AnyLayer);

      // set visible if the checkbox is checked
      if (layer.selected) {
        this.map.setLayoutProperty(source.id, 'visibility', 'visible');
      }

      // setup click event.
      this.setupClickEvent(source.id, source.info);
    });
    // Check if we are currently displaying nodes and set the visibility of the nodes layer
    if (this.displayingNodes) {
      this.map.setLayoutProperty(
        mapLayerIds.FLOW_TOOL_NODES,
        'visibility',
        'visible',
      );
    }
  }

  toggleLayer(layer: { layerName: string; selected: boolean }) {
    this.popup.remove();
    this.map.setLayoutProperty(
      layer.layerName,
      'visibility',
      layer.selected ? 'visible' : 'none',
    );
    this.displayingNodes = layer.selected;
  }

  setSelectedAreaBasedOnFeatureClick(areaName: string) {
    if (areaName && !this.displayingNodes) {
      // Call our store action that finds and sets area based on basin id
      this.$store.dispatch(
        DataFilterActions.SET_SELECTED_AREA_BY_AREA_NAME,
        areaName,
      );
      // Call our SET_DATA_FILTER to update filter selections
      this.$store.dispatch(DataFilterActions.SET_DATA_FILTER);
    }
  }
  @Watch('selectedArea')
  onSelectedAreaChange() {
    const layerId = mapLayerIds.CHOROPLETH_LAYER;
    const selectedAreaBasinId = this.selectedArea?.id;

    if (
      selectedAreaBasinId &&
      this.selectedFeatureStateItem?.properties?.basin_id !==
        selectedAreaBasinId
    ) {
      // Remove previous popup
      this.popup.remove();
      // Query rendered features and set the feature state to the feature with matching basin_id
      const features = this.map.querySourceFeatures(layerId, {
        sourceLayer: 'tile',
        filter: ['==', 'basin_id', selectedAreaBasinId || null],
      });

      if (features.length > 0) {
        // Reset the previous feature state
        if (this.selectedFeatureStateItem) {
          // Set click state to false for previously selected feature
          this.map.setFeatureState(
            {
              source: layerId,
              sourceLayer: 'tile',
              id: this.selectedFeatureStateItem.id,
            },
            { click: false },
          );
        }

        // Set the new feature state
        this.selectedFeatureStateItem = features[0];
        this.map.setFeatureState(
          {
            source: layerId,
            sourceLayer: 'tile',
            id: this.selectedFeatureStateItem.id,
          },
          { click: true },
        );
      } else {
        if (this.selectedFeatureStateItem) {
          this.map.setFeatureState(
            {
              source: layerId,
              sourceLayer: 'tile',
              id: this.selectedFeatureStateItem.id,
            },
            { click: false },
          );
          this.selectedFeatureStateItem = null;
        }
      }
    }
  }
}
